import type {
  ExpertProfitAndLossAccountComponent,
  ExpertProfitAndLossComponent,
  ExpertProfitAndLossGroupComponent,
  ExpertProfitAndLossReport,
  ExpertProfitAndLossSectionComponent,
  ExpertProfitAndLossSumComponent,
} from '../types/expertProfitAndLossReport'
import type {
  ExpertProfitAndLossPeriodColumn,
  ExpertProfitAndLossRow,
  ExpertProfitAndLossRowKind,
  ExpertProfitAndLossViewData,
} from '../types/expertProfitAndLossView'

const EMPTY_VIEW_DATA: ExpertProfitAndLossViewData = {
  periods: [],
  rows: [],
}

type Orientation = 1 | -1

const normalizeGroupChildren = (
  children: ExpertProfitAndLossGroupComponent['children'],
): ExpertProfitAndLossAccountComponent[] => {
  if (Array.isArray(children)) {
    return children
  }

  return Object.values(children)
}

const getGroupOrientation = (group: ExpertProfitAndLossGroupComponent): Orientation => {
  const children = normalizeGroupChildren(group.children)

  if (!children.length) {
    return 1
  }

  return children[0]?.nature === 'revenue' ? -1 : 1
}

const isHeadingSection = (component: ExpertProfitAndLossComponent): boolean =>
  component.type === 'section' && typeof component.label === 'string'

const getChildrenWithoutHeadings = (section: ExpertProfitAndLossSectionComponent): ExpertProfitAndLossComponent[] =>
  section.children.filter((child) => !isHeadingSection(child))

const getFirstDescendantGroupOrientation = (section: ExpertProfitAndLossSectionComponent): Orientation => {
  for (const child of getChildrenWithoutHeadings(section)) {
    if (child.type === 'group') {
      return getGroupOrientation(child)
    }

    if (child.type === 'section') {
      return getFirstDescendantGroupOrientation(child)
    }
  }

  return 1
}

const isReconciliationSection = (section: ExpertProfitAndLossSectionComponent): boolean =>
  getChildrenWithoutHeadings(section).every((child) => child.type === 'sum')

const getSumOrientation = (
  sum: ExpertProfitAndLossSumComponent,
  parent: ExpertProfitAndLossSectionComponent,
  rootSection: ExpertProfitAndLossSectionComponent,
): Orientation => {
  if (parent === rootSection) {
    return -1
  }

  if (isReconciliationSection(parent)) {
    const sumIndex = getChildrenWithoutHeadings(parent).indexOf(sum)

    return sumIndex === 0 ? -1 : 1
  }

  return getFirstDescendantGroupOrientation(parent)
}

const getRowKind = (
  component: ExpertProfitAndLossGroupComponent | ExpertProfitAndLossSumComponent,
  parent: ExpertProfitAndLossSectionComponent,
  rootSection: ExpertProfitAndLossSectionComponent,
): ExpertProfitAndLossRowKind => {
  if (component.type === 'group') {
    return 'line'
  }

  return parent === rootSection ? 'result' : 'subtotal'
}

const applyOrientation = (value: number | undefined, orientation: Orientation): number | undefined => {
  if (value === undefined) {
    return undefined
  }

  if (value === 0) {
    return 0
  }

  return orientation * value
}

const mapTotalsForPeriods = (
  columnValues: ExpertProfitAndLossGroupComponent['column_values'],
  periods: ExpertProfitAndLossPeriodColumn[],
  orientation: Orientation,
): (number | undefined)[] => periods.map((period) => applyOrientation(columnValues[period.key]?.total, orientation))

const mapVariancePercentage = (
  columnValues: ExpertProfitAndLossGroupComponent['column_values'],
  periods: ExpertProfitAndLossPeriodColumn[],
  orientation: Orientation,
): number | null | undefined => {
  const currentPeriodKey = periods[0]?.key

  if (!currentPeriodKey) {
    return undefined
  }

  const percentageChange = columnValues[currentPeriodKey]?.percentageChange

  if (percentageChange === null) {
    return null
  }

  return applyOrientation(percentageChange, orientation)
}

const computeVariance = (totals: (number | undefined)[]): number | undefined => {
  const currentTotal = totals[0]
  const previousTotal = totals[1]

  if (currentTotal === undefined || previousTotal === undefined) {
    return undefined
  }

  return currentTotal - previousTotal
}

export const selectProfitAndLossView = (report: ExpertProfitAndLossReport | undefined): ExpertProfitAndLossViewData => {
  if (!report) {
    return EMPTY_VIEW_DATA
  }

  const periods: ExpertProfitAndLossPeriodColumn[] = report.header.map((periodHeader) => ({
    key: periodHeader.key,
    label: periodHeader.label,
  }))

  const rows: ExpertProfitAndLossRow[] = []
  const rootSection = report.components[0]

  if (rootSection?.type !== 'section') {
    return { periods, rows }
  }

  const walk = (
    components: ExpertProfitAndLossComponent[],
    parent: ExpertProfitAndLossSectionComponent,
    path: string,
  ): void => {
    for (const [index, component] of components.entries()) {
      const componentPath = `${path}/${index}`

      switch (component.type) {
        case 'section':
          if (typeof component.label === 'string' && typeof component.level === 'number') {
            rows.push({
              id: componentPath,
              kind: 'heading',
              label: component.label,
              level: component.level,
              totals: periods.map(() => undefined),
              variance: undefined,
              variancePercentage: undefined,
            })
          }

          walk(component.children, component, componentPath)
          break
        case 'account':
          break
        case 'group':
        case 'sum': {
          const orientation =
            component.type === 'group'
              ? getGroupOrientation(component)
              : getSumOrientation(component, parent, rootSection)

          const totals = mapTotalsForPeriods(component.column_values, periods, orientation)
          const variancePercentage = mapVariancePercentage(component.column_values, periods, orientation)

          rows.push({
            id: componentPath,
            kind: getRowKind(component, parent, rootSection),
            label: component.label,
            level: component.type === 'sum' ? component.level : undefined,
            totals,
            variance: computeVariance(totals),
            variancePercentage,
          })
          break
        }
        default:
          ;(component) satisfies never
          break
      }
    }
  }

  walk(rootSection.children, rootSection, 'root')

  const netResultIndex = rows.map((row) => row.kind).lastIndexOf('result')

  if (netResultIndex !== -1 && netResultIndex !== rows.length - 1) {
    const [netResultRow] = rows.splice(netResultIndex, 1)

    if (netResultRow) {
      rows.push(netResultRow)
    }
  }

  return { periods, rows }
}
